/**
 * Peer — unified contact model normalizing LLM bots and worker roles
 * into a single shape: { roleId, type, displayName, mentionName, description, roleType, online, ... }
 */

function toMentionName(displayName) {
  return (displayName || 'Bot').replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
}

export function createPeerFromBot(bot) {
  return {
    roleId: bot.botId,
    type: 'llm-bot',
    displayName: bot.displayName,
    mentionName: toMentionName(bot.displayName),
    description: bot.description,
    roleType: bot.isBuiltIn ? 'host' : 'chat',
    online: true,
    botConfig: bot,
    isBuiltIn: bot.isBuiltIn || false,
  }
}

export function createPeerFromWorker(role) {
  return {
    roleId: role.roleId,
    type: 'worker',
    displayName: role.displayName,
    mentionName: toMentionName(role.displayName),
    description: role.description,
    roleType: role.roleType,
    typeName: role.typeName,
    online: role.online ?? false,
    workerPublicKey: role.roleId,
  }
}

export function createPeerFromToolAgent(toolAgent) {
  const peer = {
    roleId: '__tool_agent__',
    type: 'tool-agent',
    displayName: 'Tools',
    mentionName: 'tools',
    description: toolAgent.getDescription(),
    roleType: 'tool',
    online: true,
    isBuiltIn: true,
    toolAgent,
    refreshDescription() { peer.description = toolAgent.getDescription() },
  }
  return peer
}

export function canHost(peer) {
  return peer.type === 'llm-bot' || peer.type === 'tool-agent' || peer.roleType === 'chat'
}

/**
 * Merge LLM bots and worker roles into a single Peer list.
 * @param {Array} llmBots - Bot configs
 * @param {Array} workerRoles - Worker role objects
 * @param {string[]} [excludeIds] - Bot IDs to exclude (e.g. built-in bots handled separately)
 */
export function mergePeers(llmBots, workerRoles, excludeIds) {
  const exclude = excludeIds ? new Set(excludeIds) : null
  const botPeers = (llmBots || [])
    .filter(b => !exclude || !exclude.has(b.botId))
    .map(createPeerFromBot)
  const workerPeers = (workerRoles || []).map(createPeerFromWorker)
  return [...botPeers, ...workerPeers]
}
